import React from 'react';

import { Box, Flex, Text, TextProps } from '@opendoor/bricks/core';
import { centsToCurrencyString } from '@opendoor/shared-fe/build/formatUtils';
import { LAYOUT_CONTENT_PADDING } from 'platform/components/layout/Layout';
import { ExternalLink } from 'platform/react-router';

export const AGENT_BONUS_PERCENTAGE = 0.01;

interface IAgentCommissionBox {
  eligibleForAgentReferralPayment?: boolean;
  fetchingAgentReferralPayments: boolean;
  agentCommissionCents: number;
  agentCommissionPercentOfHeadline: number;
}

/**
 * Shows agent-commission-box
 *
 * component-test-check-ignore
 */
export const AgentCommissionBox: React.FC<IAgentCommissionBox> = ({
  eligibleForAgentReferralPayment,
  agentCommissionCents,
  fetchingAgentReferralPayments,
  agentCommissionPercentOfHeadline,
}) => {
  let commissionText = '';
  if (fetchingAgentReferralPayments) {
    commissionText = 'Loading...';
  } else if (eligibleForAgentReferralPayment !== undefined) {
    if (eligibleForAgentReferralPayment) {
      commissionText = centsToCurrencyString(agentCommissionCents);
    } else {
      commissionText = 'Not eligible';
    }
  }

  return (
    <Box
      backgroundColor="neutrals0"
      p={LAYOUT_CONTENT_PADDING}
      borderRadius={['semiRounded', null, 'roundedSmooth']}
      borderWidth="1px"
      borderColor="neutrals40"
      borderStyle="solid"
      data-testid="agent-commission-box"
    >
      <Flex mb={4} flexDirection={['column', null, 'row']} align-items="center">
        <Text fontSize={['s2', null, 's3']} lineHeight={['s2', null, 's3']} fontWeight="bold">
          Your {agentCommissionPercentOfHeadline}% commission
          {(eligibleForAgentReferralPayment !== undefined || fetchingAgentReferralPayments) && ':'}
          &nbsp;
        </Text>
        <Text mt={1} fontSize={['s2', null, 's3']} lineHeight={['s2', null, 's3']}>
          {commissionText}
        </Text>
      </Flex>
      <Flex>
        {!fetchingAgentReferralPayments && (
          <AgentCommissionBoxBottomText
            eligibleForAgentReferralPayment={eligibleForAgentReferralPayment}
            agentCommissionPercentOfHeadline={agentCommissionPercentOfHeadline}
            size="s1"
          />
        )}
      </Flex>
    </Box>
  );
};

type AgentCommissionBoxBottomTextProps = Pick<
  IAgentCommissionBox,
  'eligibleForAgentReferralPayment' | 'agentCommissionPercentOfHeadline'
> &
  TextProps;

/**
 * Shows agent-commission-box-bottom-text
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const AgentCommissionBoxBottomText: React.FC<AgentCommissionBoxBottomTextProps> = ({
  eligibleForAgentReferralPayment,
  agentCommissionPercentOfHeadline,
  ...textProps
}) => {
  if (eligibleForAgentReferralPayment) {
    return (
      <Text as="span" textAlign={'start'} {...textProps}>
        Opendoor Brokerage will pay your {agentCommissionPercentOfHeadline}% commission at closing.
        This is in addition to any commission from your client and any applicable Agent Access
        bonus.
      </Text>
    );
  }
  if (eligibleForAgentReferralPayment === undefined) {
    return (
      <Text as="span" textAlign={'start'} {...textProps}>
        Something went wrong. Try again in a few minutes, or contact your Agent Experience Partner.
      </Text>
    );
  }

  return (
    <Text as="span" textAlign={'start'} {...textProps}>
      Visit the{' '}
      <ExternalLink
        analyticsName="offer-breakdown-agent-accesss"
        href="https://www.opendoor.com/w/agent-access"
        title="Opendoor Agent Access"
        target="_blank"
        aria-label="Opendoor Agent Access"
      >
        Agent Access page
      </ExternalLink>{' '}
      to review the eligibility criteria for receiving {agentCommissionPercentOfHeadline}%
      commission. If this is an error, please contact your Agent Experience Partner.
    </Text>
  );
};
